import type { FormatCurrencyOptions } from './types';

class FormatService {
  private static MAX_DISPLAYED_COUNTER = 20;

  public static formatNumber(
    value: string | number | null | undefined,
    preformat?: null | ((number: number) => number),
    formatterArgs?: Intl.NumberFormatOptions,
  ) {
    const emptyPlaceholder = '';

    if (value === null || value === undefined || value === '') {
      return emptyPlaceholder;
    }

    const numberValue = typeof value === 'number' ? value : Number(value.replace(/,/g, ''));

    if (isNaN(numberValue)) {
      return emptyPlaceholder;
    }

    const formatter = new Intl.NumberFormat('en-US', formatterArgs);

    return formatter.format(preformat ? preformat(numberValue) : numberValue);
  }

  public static formatPercent(value: string | number | null | undefined) {
    return FormatService.formatNumber(value, (number) => number / 100, {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  public static formatLabelText(labelText?: string | { label: string; [key: string]: string }) {
    if (!labelText) return labelText;
    if (typeof labelText === 'string') return labelText.replace(/ /g, '');
    return labelText.label.replace(/ /g, '');
  }

  public static formatUnit(value: string | number | null | undefined) {
    return FormatService.formatNumber(value, null, {
      style: 'decimal',
    });
  }

  public static formatCurrency(
    value: string | number | null | undefined,
    options: FormatCurrencyOptions = {},
  ): string | number {
    const { currency = 'USD', style = 'currency', emptyPlaceholder = '', hideNegativeSign = false } = options;

    // If value is null, undefined, or empty string, show as blank
    if (value === null || value === undefined || value === '') {
      return emptyPlaceholder;
    }

    const numberValue = typeof value === 'number' ? value : Number(value.replace(/,/g, ''));

    // If value cannot be converted to a number, show as blank
    if (isNaN(numberValue)) {
      return emptyPlaceholder;
    }

    const isNegative = numberValue < 0;

    // Format the number, including zero values (e.g., $0.00)
    const formattedValue = new Intl.NumberFormat('en-US', {
      style,
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(numberValue));

    return isNegative && !hideNegativeSign ? `-${formattedValue}` : formattedValue;
  }

  public static formatCounter(count?: number) {
    if (!count && count !== 0) return;
    return count > this.MAX_DISPLAYED_COUNTER ? `${this.MAX_DISPLAYED_COUNTER}+` : count.toString();
  }

  public static roundNumber(value: number | string) {
    return Math.round((+value + Number.EPSILON) * 100) / 100;
  }
}

export default FormatService;
